import cn from "classnames";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AudioMessage } from "../../aiConversations.types";
import { AssistantAvatar } from "../AssistantAvatar";
import { BubbleMeta } from "../BubbleMeta";

import styles from "./AudioBubble.module.scss";

interface Props {
  message: AudioMessage;
  incomingAvatar?: string;
  incomingName?: string;
  // What the assistant heard, from the turn snapshot (WhatsApp only; absent
  // until a reply to this voice note has been opened in "Correct this reply").
  transcript?: string;
}

// Only one audio plays at a time across the whole thread.
let currentlyPlaying: HTMLAudioElement | null = null;

const PlayIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path d="M0.5 1L13 8L0.5 15V1Z" fill="#090914" />
  </svg>
);

const PauseIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <rect x="1.5" y="1" width="3.5" height="14" rx="1" fill="#090914" />
    <rect x="9" y="1" width="3.5" height="14" rx="1" fill="#090914" />
  </svg>
);

const IncomingAvatar = ({ name, url }: { name: string; url?: string }) => {
  if (url) {
    return <img className={styles.incomingAvatarImg} src={url} alt={name} />;
  }
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return <div className={styles.incomingAvatar}>{initials}</div>;
};

const formatSeconds = (value: number) => {
  if (!Number.isFinite(value) || value < 0) return "0:00";
  const total = Math.floor(value);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const AudioBubble = ({
  message,
  incomingAvatar,
  incomingName = "?",
  transcript,
}: Props) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const isOutgoing = message.direction === "outgoing";

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
  };

  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;

  return (
    <div
      className={cn(styles.row, {
        [styles.rowOutgoing]: isOutgoing,
        [styles.rowIncoming]: !isOutgoing,
      })}
    >
      {!isOutgoing && (
        <IncomingAvatar name={incomingName} url={incomingAvatar} />
      )}

      <div
        className={cn(styles.bubbleWrapper, {
          [styles.bubbleWrapperOutgoing]: isOutgoing,
        })}
      >
        <div className={styles.bubble}>
          <button
            type="button"
            className={styles.playButton}
            aria-label={isPlaying ? "Pause audio" : "Play audio"}
            onClick={togglePlay}
            disabled={!message.mediaUrl}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <div className={styles.timing}>
            {formatSeconds(currentTime)} / {formatSeconds(duration)}
          </div>
          <div className={styles.progress}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          {message.mediaUrl ? (
            <audio
              ref={audioRef}
              src={message.mediaUrl}
              preload="metadata"
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
              onPlay={(e) => {
                const audio = e.currentTarget;
                if (currentlyPlaying && currentlyPlaying !== audio) {
                  currentlyPlaying.pause();
                }
                currentlyPlaying = audio;
                setIsPlaying(true);
              }}
              onPause={() => setIsPlaying(false)}
              onEnded={(e) => {
                if (currentlyPlaying === e.currentTarget) {
                  currentlyPlaying = null;
                }
                e.currentTarget.currentTime = 0;
                setIsPlaying(false);
                setCurrentTime(0);
              }}
            />
          ) : null}
        </div>
        {transcript ? (
          <div className={styles.transcript}>
            <span className={styles.transcriptLabel}>
              {t("aiTurn.voiceTranscript")}
            </span>
            <span className={styles.transcriptText}>{transcript}</span>
          </div>
        ) : null}
        <BubbleMeta message={message} />
      </div>

      {isOutgoing && <AssistantAvatar letter="A" size={24} />}
    </div>
  );
};

export default AudioBubble;
